"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { isAuthenticated } from "../lib/auth";
import { setUnauthorizedHandler } from "../lib/fetch-interceptor";

type AuthGuardProps = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isChecking, setIsChecking] = useState(true);

  // Register unauthorized handler for 401 errors
  useEffect(() => {
    const handleUnauthorized = () => {
      // Token was already removed by fetch interceptor
      // Redirect to login with current path as redirect parameter
      const currentPath = pathname === "/login" ? "/" : pathname;
      const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
      router.push(loginUrl);
    };

    setUnauthorizedHandler(handleUnauthorized);

    return () => {
      // Clear handler on unmount
      setUnauthorizedHandler(() => {});
    };
  }, [router, pathname]);

  useEffect(() => {
    // Handle login page
    if (pathname === "/login") {
      // If user is already authenticated, redirect to home
      if (isAuthenticated()) {
        const redirectUrl =
          new URLSearchParams(window.location.search).get("redirect") || "/";
        router.push(redirectUrl);
        return;
      }
      // If not authenticated, allow access to login page
      setIsChecking(false);
      return;
    }

    // For all other pages, check authentication
    if (!isAuthenticated()) {
      const loginUrl = `/login?redirect=${encodeURIComponent(pathname)}`;
      router.push(loginUrl);
      return;
    }

    setIsChecking(false);
  }, [router, pathname]);

  // Show nothing while checking (or show a loading spinner)
  if (isChecking && pathname !== "/login") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-zinc-500 dark:text-zinc-400">Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
}
